import { fetchEpoch } from "@/server/services/epochService";
import { resolveCSK } from "@/middleware/epochKeyCache";
import {
  updateChatInLocalDB,
  getChatFromLocalDB,
} from "@/indexedDB/services/chats";
import { rotateCSK } from "@/middleware/chats";
import { isUserAuthenticated } from "@/middleware/auth";
import { ChatType } from "@/server/types/chat";

// ---------------------------------------------------------------------------
// Event payload types
// ---------------------------------------------------------------------------

export type KeyRotatedEventType = {
  chat_id: string;
  new_epoch: number;
  reason?: string;
};

export type RotationRequestedEventType = {
  chat_id: string;
  reason?: string;
};

// ---------------------------------------------------------------------------
// key_rotated handler (spec §10.2)
// ---------------------------------------------------------------------------

/**
 * Handles the `key_rotated` socket event.
 *
 * 1. Fetch own eCSK for the new epoch from the server.
 * 2. Decapsulate → cache (via resolveCSK).
 * 3. Update local chat state with the new current_epoch + eCSK.
 */
export const handleKeyRotatedEvent = async (
  data: KeyRotatedEventType,
): Promise<void> => {
  const { chat_id, new_epoch } = data;

  try {
    // Fetch the epoch record (includes our eCSK)
    const epochRecord = await fetchEpoch(chat_id, new_epoch);
    const auth = isUserAuthenticated();
    if (!auth) {
      return;
    }

    const ownECSK = epochRecord.self_payload;
    if (!ownECSK) {
      console.warn(
        `key_rotated: no eCSK for self in epoch ${new_epoch} of chat ${chat_id}`,
      );
      return;
    }

    // Decapsulate and cache (hot + warm)
    await resolveCSK(chat_id, new_epoch, ownECSK);

    // Update local chat state
    await updateChatInLocalDB(chat_id, {
      currentEpoch: new_epoch,
      eCSK: ownECSK,
    });
  } catch (error) {
    console.error(`key_rotated handler failed for chat ${chat_id}:`, error);
  }
};

// ---------------------------------------------------------------------------
// rotation_requested handler (spec §7.2)
// ---------------------------------------------------------------------------

/**
 * Handles the `rotation_requested` socket event.
 *
 * The server asks a client to perform the rotation (server-initiated trigger,
 * e.g. time-based). The first client to respond executes the full protocol.
 */
export const handleRotationRequestedEvent = async (
  data: RotationRequestedEventType,
): Promise<void> => {
  const { chat_id, reason } = data;

  try {
    const auth = isUserAuthenticated();
    if (!auth) {
      return;
    }

    // Only rotate group chats (P2P and notes don't support rotation)
    const chatDetails = await getChatFromLocalDB(chat_id);
    if (!chatDetails || chatDetails.type !== ChatType.GROUP) {
      return;
    }

    await rotateCSK(chat_id, reason ?? "server_requested");
  } catch (error) {
    // Another client may have already rotated (409 conflict) — that's OK.
    console.warn(
      `rotation_requested handler failed for chat ${chat_id}:`,
      error,
    );
  }
};
